import axios from "axios";
import _ from "lodash";
import { source_entity } from "../context";
import type { MarketType } from "../definitions";
import type { HotelBookPayload, HotelPrebook } from "../definitions/hotels";

const environment = import.meta.env.VITE_APP_ENVIRONMENT;

export let canaveral_url = import.meta.env.VITE_APP_ENDPOINT;
canaveral_url = `${canaveral_url}/${environment === "prod" ? "prod" : "uncached"}`;

export let sisi_api_url = import.meta.env.VITE_APP_SISIAPI_ENDPOINT;
sisi_api_url = `${sisi_api_url}/${environment === "prod" ? "prod" : "staging"}`;

export let sisi2_api_url = import.meta.env.VITE_APP_SISI2_API_ENDPOINT;
sisi2_api_url = `${sisi2_api_url}/${environment === "prod" ? "prod" : "staging"}`;

export let valaquenta_api_url = import.meta.env.VITE_APP_VALAQUENTA_ENDPOINT;
valaquenta_api_url = `${valaquenta_api_url}/${environment === "prod" ? "prod" : "staging"}`;

export let apollo_api_url = import.meta.env.VITE_APP_APOLLO_ENDPOINT;
apollo_api_url = `${apollo_api_url}/${environment === "prod" ? "prod" : "staging"}`;

const sisikong_url_staging = import.meta.env.VITE_APP_SISIKONG;
const sisikong_url_prod = import.meta.env.VITE_APP_SISIKONG_PROD;
export const sisikong_url = environment === "prod" ? sisikong_url_prod : sisikong_url_staging;

export const website_api_key = import.meta.env.VITE_APP_WEBSITE_KEY;

export async function fetchWebsiteMeta(api_key: string): Promise<{ [key: string]: any } | null> {
  try {
    const response = await axios.get(`${canaveral_url}/website-meta/${api_key}/`);
    return response.data;
  } catch {
    return null;
  }
}

// ============================== ADDONS =============================

export async function fetchAddOnsPromo(params: {
  [key: string]: string;
}): Promise<{ [key: string]: any } | null> {
  try {
    const response = await axios.get(`${canaveral_url}/addons-promo/`, {
      params,
    });
    return response?.data ?? [];
  } catch {
    return null;
  }
}

export async function fetchAddOnDetails(reference: string, source_entity: string) {
  try {
    const response = await axios.get(`${canaveral_url}/addon-details/${reference}/`, {
      params: { source_entity },
    });
    return response?.data ?? {};
  } catch {
    return null;
  }
}

export async function fetchAddOnPrice({
  market_type = "B2C",
  source_entity,
  reference,
  date,
  adults,
  adult_ages,
  children,
  children_ages,
  rooms = [],
}) {
  const params = {
    market_type,
    execution: date,
    adults,
    adult_ages: adult_ages.join(","),
    children,
    children_ages: children_ages.join(","),
    source_entity,
  };

  if (rooms.length) {
    params["rooms"] = JSON.stringify(rooms);
  }

  try {
    const response = await axios.get(`${canaveral_url}/addon-price-search/${reference}/`, {
      params,
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function fetchAddOnsMetadata({ source_entity }: { source_entity: string }) {
  try {
    const response = await axios.get(`${canaveral_url}/addon-metadata/`, {
      params: { source_entity },
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function fetchAddOnsList({
  source_entity,
  website_api_key,
  destination_code,
  categories,
}) {
  try {
    const response = await axios.get(`${canaveral_url}/addon-list/`, {
      params: {
        source_entity,
        website_api_key,
        destination_code: destination_code.map((code: string) => window.btoa(code)).join(","),
        categories: categories.join(","),
      },
    });
    return _.get(response, "data.results", []);
  } catch {
    return null;
  }
}

export async function createPaymentIntent({
  amount,
  currency,
  description,
  source_entity,
}: {
  amount: number;
  currency: string;
  description: string;
  source_entity: string;
}) {
  try {
    const response = await axios.post(`${canaveral_url}/b2c-payment-intent/`, {
      amount,
      currency,
      description,
      source_entity,
    });

    return _.get(response, "data", {});
  } catch {
    return null;
  }
}

export async function createStripePaymentCheckout({
  amount,
  balance_amount,
  balance_due_date,
  product_name,
  currency,
  description,
  source_entity,
  website_api_key,
  customer_email = null,
  customer_stripe_id = null,
  success_url,
  cancel_url,
  metadata,
}) {
  const payload = {
    amount,
    balance_amount,
    balance_due_date,
    product_name,
    currency,
    description,
    source_entity,
    website_api_key,
    success_url,
    cancel_url,
    metadata,
  };

  if (customer_email) {
    payload.customer_email = customer_email;
  }

  if (customer_stripe_id) {
    payload.customer_id = customer_stripe_id;
  }

  try {
    const response = await axios.post(`${canaveral_url}/payment-checkout/`, payload);

    if (response.status >= 400) {
      return null;
    }

    return response?.data ?? {};
  } catch {
    return null;
  }
}

export async function fetchPaymentCheckout({
  checkout_id,
  source_entity,
}: {
  checkout_id: string;
  source_entity: string;
}) {
  const params = { source_entity };

  try {
    const response = await axios.get(`${canaveral_url}/payment-checkout/${checkout_id}`, {
      params,
    });

    if (response.status >= 400) {
      return null;
    }

    return _.get(response, "data", {});
  } catch {
    return null;
  }
}

export async function executeAddonBooking({ payload }: { payload: any }) {
  try {
    const response = await axios.post(`${canaveral_url}/book-addon/`, payload);
    return response.data;
  } catch {
    return null;
  }
}

export async function autocompletePOI({ query }: { query: string }) {
  try {
    const response = await axios.get(`${canaveral_url}/poi-autocomplete`, {
      params: { query },
    });

    return response.data;
  } catch {
    return null;
  }
}

// ============================== PACKAGES =============================

export async function fetchPackagesPromo({ references__in }: { references__in: string[] }) {
  try {
    const response = await axios.get(`${canaveral_url}/packages-promo/`, {
      params: {
        references__in,
        source_entity,
        api_key: website_api_key,
      },
    });
    return _.get(response, "data.results", []);
  } catch {
    return null;
  }
}

export async function fetchPackageDetails({
  reference,
  source_entity,
  api_key,
}: {
  reference: string;
  source_entity: string;
  api_key: string;
}) {
  return await axios.get(`${canaveral_url}/package-details/${reference}/`, {
    params: { source_entity, api_key },
  });
}

export async function fetchPackagesMetadata({
  source_entity,
  api_key,
}: {
  source_entity: string;
  api_key: string;
}) {
  try {
    const response = await axios.get(`${canaveral_url}/packages-metadata/`, {
      params: { source_entity, api_key },
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function fetchPackagesList({
  api_key,
  source_entity,
  destination_codes,
  durations,
  services,
}: {
  api_key: string;
  source_entity: string;
  destination_codes: string[];
  durations: string[];
  services: string[];
}) {
  try {
    const response = await axios.get(`${canaveral_url}/package-list/`, {
      params: {
        source_entity,
        destination_codes: destination_codes.map((code) => window.btoa(code)).join(","),
        durations: durations.join(","),
        services: services.join(","),
        api_key,
        limit: 100,
        // TODO: should only fetch results based on the relevant theme
        // multi_srv: true,
      },
    });

    return _.get(response, "data.results", []);
  } catch {
    return null;
  }
}

// =========================== COUNTRY INFO ==========================

export async function fetchCountriesInfo({ country_codes }: { country_codes: string[] }) {
  try {
    const response = await axios.get(`${canaveral_url}/country-info/`, {
      params: { code__in: country_codes.join(",") },
    });
    return _.get(response, "data", []);
  } catch {
    return [];
  }
}

// ========================== PACKAGE SEARCH =========================
export async function initPackageSearch({
  api_key,
  reference,
  source_entity,
  adults,
  children,
  pax_setup,
  start_date,
  flight_origin,
}) {
  try {
    const response = await axios.get(`${canaveral_url}/package-search/${reference}/`, {
      params: {
        api_key,
        source_entity,
        adults,
        children,
        pax_setup: JSON.stringify(pax_setup),
        start_date,
        flight_origin,
      },
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function pollPackageSearch({
  reference,
  session_id,
}: {
  reference: string;
  session_id: string;
}) {
  try {
    const response = await axios.get(
      `${canaveral_url}/package-search/${reference}/poll_search_status/`,
      {
        params: {
          session_id,
        },
      },
    );
    return response.data;
  } catch {
    return null;
  }
}

export async function fetchPackagePrice({ reference, payload }) {
  try {
    const response = await axios.post(
      `${canaveral_url}/package-search/${reference}/price_package/`,
      payload,
    );
    return response.data;
  } catch {
    return null;
  }
}

// ======================= ACCOMMODATION SEARCH ======================

export async function initiateAccommodationSearch({ payload }) {
  try {
    const response = await axios.post(`${sisikong_url}/acc/gateway/initiate_search/`, payload);
    return response.data;
  } catch {
    return null;
  }
}

export async function pollAccommodationSearch({
  session_id,
  result_id = null,
  market_type = "B2C",
  lean = true,
}: {
  session_id: string;
  result_id?: string;
  market_type?: MarketType;
  lean?: boolean;
}) {
  try {
    const params = { session_id, market_type, lean };
    if (result_id) {
      params.result_id = result_id;
    }

    const response = await axios.get(`${sisikong_url}/acc/gateway/fetch_results/`, {
      params,
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function getAccommodationDetails({
  session_id,
  acc_id,
  market_type = "B2C",
}: {
  session_id: string;
  acc_id: string;
  market_type?: MarketType;
}) {
  try {
    const params = { session_id, acc_id, market_type };
    const response = await axios.get(`${sisikong_url}/acc/gateway/search_details/`, {
      params,
    });

    if (response.status >= 400) {
      return null;
    }

    return response.data;
  } catch {
    return null;
  }
}

export async function fetchCxlDetails({
  session_id,
  acc_id,
  booking_code,
  supplier,
  market_type = "B2C",
}: {
  session_id: string;
  acc_id: string;
  booking_code: string;
  supplier: string;
  market_type?: MarketType;
}) {
  try {
    const response = await axios.get(`${sisikong_url}/acc/gateway/cxl_details/`, {
      params: {
        session_id,
        acc_id,
        booking_code,
        supplier,
        market_type,
      },
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function prebookAccommodation({
  session_id,
  acc_id,
  booking_code,
  market_type,
  uids,
  supplier,
}: {
  session_id: string;
  acc_id: string;
  booking_code: string[];
  market_type: MarketType;
  uids: string[];
  supplier: string;
}): Promise<HotelPrebook | null> {
  try {
    const response = await axios.post(`${sisikong_url}/acc/gateway/prebook/`, {
      session_id,
      acc_id,
      booking_code,
      market_type,
      uids,
      supplier,
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function executeAccBooking({ payload }: { payload: HotelBookPayload }) {
  try {
    const response = await axios.post(`${sisikong_url}/crs/b2c-accommodation-bundle/`, payload);
    return response.data;
  } catch {
    return null;
  }
}

// ========================== FLIGHT SEARCH ==========================

export async function fetchFlightSearchResults({
  session_id,
  search_uid,
  page_size = 1,
  ordering = "price",
}: {
  session_id: string;
  search_uid: string;
  page_size?: number;
  ordering?: string;
}) {
  try {
    const response = await axios.get(`${canaveral_url}/flight-search-poll/${session_id}`, {
      params: {
        search_uid,
        page_size,
        ordering,
      },
    });
    return response.data;
  } catch {
    return null;
  }
}

// ========================= TRANSFER SEARCH =========================
export async function initTransferSearch({
  reference,
  session_id,
  payloads,
}: {
  reference: string;
  session_id: string;
  payloads: any;
}) {
  try {
    const response = await axios.post(
      `${canaveral_url}/package-search/${reference}/init_transfers_search/`,
      { session_id, payloads },
    );
    return response.data;
  } catch {
    return null;
  }
}

export async function fetchTransferSearchResults({
  session_id,
  params = {},
}: {
  session_id: string;
  params: any;
}) {
  try {
    const response = await axios.get(`${canaveral_url}/transfer-search-poll/${session_id}/`, {
      params,
    });
    return response.data;
  } catch {
    return null;
  }
}

// ========================= DESTINATION INFO ========================
export async function fastDestAutoComplete({ name }: { name: string }) {
  try {
    const response = await axios.get(
      `${sisikong_url}/veritascms/geoapi/fast_aggregate_autocomplete/`,
      { params: { name } },
    );
    return response.data;
  } catch {
    return null;
  }
}

// ============================= PROFILE =============================
export async function fetchGuestProfile(user_id: string) {
  try {
    const response = await axios.get(`${sisi_api_url}/guest-crm/${encodeURIComponent(user_id)}/`, {
      params: {
        requester_website: website_api_key,
        requester_source_entity: source_entity,
      },
    });
    return response.data;
  } catch {
    return null;
  }
}

export async function updateGuestProfile({ user_id, payload }: { user_id: string; payload: any }) {
  try {
    const result = await axios.patch(`${sisi_api_url}/guest-crm/${encodeURIComponent(user_id)}/`, {
      ...payload,
      requester_website: website_api_key,
      requester_source_entity: source_entity,
    });
    return result.data;
  } catch {
    return null;
  }
}

export * from "./authentication";
export * from "./autocomplete";
export * from "./flights";
export * from "./hotels";
export * from "./registration";
export * from "./search_session";
